import React, { useEffect } from 'react';
import styled from 'styled-components';
import { usePageStore } from '../../shared/store/pageStore';
import { useWorkListActions } from '../../shared/hooks/useApi/useWorkList';
import { useAuth } from '../../shared/hooks/useApi/useAuth';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { DeviceType, PerPage } from '../../shared/enum/EnumRepository';
import { useWorkListStore } from '../../shared/store/WorkListStore';
import { LIMIT_PAGE_NUM } from '../../shared/constants/limits';
import PageSizeStation from '../../shared/component/PageSizeStation';
import { useWorkViewStore } from '../../shared/store/WorkViewStore';
import { useDeviceStore } from '../../shared/store/deviceStore';
import { useUnsavedChanges } from '../../shared/hooks/useUnsavedChanges';
import { useWorkTagStore } from '../../shared/store/workTagStore';

const PageAboutWork: React.FC = () => {
  const { isEditMode } = useEditMode();
  const { isOwner } = useAuth();
  const { page } = usePageStore();
  const { getWorkList, getWorkListOwner } = useWorkListActions();
  const pageSize = useWorkListStore((s) => s.pageSize);
  const setPageSize = useWorkListStore((s) => s.setPageSize);
  const sortData = useWorkListStore((s) => s.sortData);
  const filterData = useWorkListStore((s) => s.filterData);
  const selectedTags = useWorkTagStore((s) => s.selectedTags);
  const { requestExitEditMode } = useUnsavedChanges();
  const activeWork = useWorkViewStore((s) => s.activeWork);
  const { deviceType } = useDeviceStore();
  const [currentWindow, setCurrentWindow] = React.useState(1);
  const isMobile = deviceType === DeviceType.MOBILE;
  // 모바일에서 activeWork가 있을 때 PageAboutWork를 숨기기 위한 플래그
  // WorkListMobile의 WorkListComp와 동일한 애니메이션 효과를 위해 사용
  const isActive = isMobile && !!activeWork;

  const fetchWorkPage = (pageNum: number) => {
    const meta = {
      page: pageNum,
      size: pageSize,
      sortData,
      filterData: { ...filterData, tagIds: selectedTags.map((t) => t.id) },
    };
    if (isOwner) {
      void getWorkListOwner(meta);
    } else {
      void getWorkList(meta);
    }
  };

  // 편집 모드에서는 페이지만 갱신되어 activeWork·멀티 선택과 리스트 행이 어긋날 수 있어 이동 차단
  const handlePageClick = (pageNum: number) => {
    if (isEditMode) {
      requestExitEditMode({ confirmWhenClean: true });
      return;
    }
    fetchWorkPage(pageNum);
  };

  // 편집 모드에서는 페이지만 갱신되어 activeWork·멀티 선택과 리스트 행이 어긋날 수 있어 이동 차단
  const handlePageWindowClick = (direction: 'prev' | 'next') => {
    if (isEditMode) {
      requestExitEditMode({ confirmWhenClean: true });
      return;
    }

    if (direction === 'prev' && currentWindow > 1) {
      const newPage = (currentWindow - 2) * LIMIT_PAGE_NUM + 1;
      fetchWorkPage(newPage);
      setCurrentWindow(currentWindow - 1);
    } else if (direction === 'next' && currentWindow < Math.ceil(page.totalPages / LIMIT_PAGE_NUM)) {
      const newPage = currentWindow * LIMIT_PAGE_NUM + 1;
      fetchWorkPage(newPage);
      setCurrentWindow(currentWindow + 1);
    }
  };

  // Calculate the start and end page numbers for the current window
  const startPage = (currentWindow - 1) * LIMIT_PAGE_NUM + 1;
  const endPage = Math.min(startPage + LIMIT_PAGE_NUM - 1, page.totalPages);

  // Update window if current page is outside the visible range
  useEffect(() => {
    const windowForCurrentPage = Math.ceil(page.page / LIMIT_PAGE_NUM);
    if (windowForCurrentPage !== currentWindow) {
      setCurrentWindow(windowForCurrentPage);
    }
  }, [page.page]);

  const handlePerPageChange = (value: PerPage) => {
    setPageSize(value);
  }

  return (
    <PageContainer $isActive={isActive}>
      <PageButtonContainer>
      {currentWindow > 1 && (
        <PageWindowButton onClick={() => handlePageWindowClick('prev')}>←</PageWindowButton>
      )}
      {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i)
        .map((pageNum) => (
          pageNum === page.page ? (
            <CurrPageButton key={pageNum}>{pageNum}</CurrPageButton>
          ) : (
            <PageButton key={pageNum} onClick={() => handlePageClick(pageNum)}>{pageNum}</PageButton>
          )
        ))}
      {currentWindow < Math.ceil(page.totalPages / LIMIT_PAGE_NUM) && (
        <PageWindowButton onClick={() => handlePageWindowClick('next')}>→</PageWindowButton>
      )}
      </PageButtonContainer>
      <PageSizeStationWrapper>
        <PageSizeStation 
          currentValue={pageSize} 
          handlePerPageChange={handlePerPageChange} 
          direction={true} />
      </PageSizeStationWrapper>
    </PageContainer>
  );
}

export default PageAboutWork;

const PageContainer = styled.div<{ $isActive: boolean }>`
  width: ${({ $isActive }) => $isActive ? '0%' : '100%'};
  opacity: ${({ $isActive }) => $isActive ? 0 : 1};
  height: ${({ $isActive }) => $isActive ? '0%' : 'calc(3.5vh)'};
  max-height: 30px;
  display: flex;
  align-items: center;
  gap: 30px;
  ${({ theme }) => theme.typography.Body_04};
  transition: all 0.7s ease-in-out;
  overflow: visible;
`;

export const PageButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const PageButton = styled.div`
  width: fit-content;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 2px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  transition: all 0.2s ease-in;
  &:hover {
    transform: scale(1.1);
    color: ${({ theme }) => theme.colors.color_Gray_04};
  }
`;

export const CurrPageButton = styled(PageButton)`
  color: ${({ theme }) => theme.colors.color_Gray_02};
  cursor: default;
`;

export const PageWindowButton = styled(PageButton)`
`;

const PageSizeStationWrapper = styled.div`
  overflow: visible;
  position: relative;
  z-index: 4;
`;
